import React, { useContext } from 'react';
import { AuthContext } from 'contexts';
import { useHistory } from 'react-router';
import { message } from '@bb/elements-web';
import { useAnsweringServiceDetails } from 'hooks';
import { cancelAnsweringServiceSubscription } from 'api/AnsweringServiceController';
import { SupraSubscriptionProductBillingModel } from '@bb/domain/dist/types';
import useOrgBillingModel from 'hooks/useOrgBillingModel';
import useOverduePaymentStage from 'hooks/useOverduePaymentStage';
import styled from 'styled-components';
import AlertBanner from './AlertBanner';

/**
 * A group of alert banners to show
 * - when the logged-in user has an outstanding payment due.
 * - when the logged-in user's organization has an outstanding payment due.
 * - (temporary) when the logged-in user has not migrated their answering service subscription to the new billing system.
 */
const BillingAlertBanners: React.FC = () => {
  const { auth } = useContext(AuthContext);
  const history = useHistory();

  const isAdmin = auth.roles[auth.role] >= auth.roles.BRANCH_ADMIN;

  const {
    answeringServicePayer,
    isMigratedToNewBilling,
    answeringSubscription,
    setAnsweringSubscription,
  } = useAnsweringServiceDetails({
    userId: auth.id,
  });

  const { supraSubscriptionPayer } = useOrgBillingModel();
  const {
    isUserSuspended,
    isOrgSuspended,
    isUserDeactivated,
    isOrgDeactivated,
  } = useOverduePaymentStage();

  // Determines if the logged-in user is responsible for paying for their own plan.
  const isUserPaying =
    answeringServicePayer === 'user' ||
    supraSubscriptionPayer === SupraSubscriptionProductBillingModel.Keyholder;

  // Determines if the org/brokerage is responsible for paying for the logged-in user's plan.
  // The supra subscription does not need to be considered in this check since org/brokerage is not an
  // option for who can pay.
  const isOrgPaying = answeringServicePayer === 'brokerage';

  const goToPayNowModalUser = () => {
    history.push(`/subscriptions_payments/user?payNow=true`);
  };
  const goToPayNowModalOrg = () => {
    history.push(`/subscriptions_payments/org?payNow=true`);
  };

  const goToLCSSubscriptionFlow = async () => {
    try {
      // cancel the current subscription and redirect to the LCS subscription flow
      await cancelAnsweringServiceSubscription(answeringSubscription?.id);
      setAnsweringSubscription(null);

      history.push(
        `/answering_service/${answeringServicePayer === 'brokerage' ? 'org' : 'user'}`,
      );
    } catch (error) {
      message.error('Something went wrong. Please try again later.');
    }
  };

  const banners: React.ComponentProps<typeof AlertBanner>[] = [];

  if (isUserPaying) {
    if (isUserSuspended) {
      banners.push({
        message:
          'Your service(s) have been suspended due to an outstanding payment, please update your credit card.',
        canCloseBanner: false,
        onButtonClick: goToPayNowModalUser,
        buttonText: 'Pay Now',
      });
    }

    if (isUserDeactivated) {
      banners.push({
        message:
          'You have been unsubscribed from your service(s) due to an outstanding payment.',
        canCloseBanner: true,
      });
    }
  }

  if (isOrgPaying) {
    if (isOrgSuspended) {
      if (isAdmin) {
        banners.push({
          message:
            "Your brokerage service(s) have been suspended due to an outstanding payment. Please update your brokerage's card to resume service.",
          canCloseBanner: false,
          onButtonClick: goToPayNowModalOrg,
          buttonText: 'Pay Now',
        });
      } else {
        banners.push({
          message:
            'Your service(s) have been suspended due to an outstanding payment. Please contact your brokerage for more details.',
          canCloseBanner: false,
        });
      }
    }

    if (isOrgDeactivated) {
      banners.push({
        message:
          'You have been unsubscribed from your service(s) due to an outstanding payment. Please contact your brokerage for more information.',
        canCloseBanner: true,
      });
    }
  }

  // If the user has not migrated to the new billing system, show a banner prompting them to re-subscribe.
  if (!isMigratedToNewBilling && !!answeringSubscription) {
    banners.push({
      message:
        answeringServicePayer === 'brokerage'
          ? 'We’ve transitioned to a new payment provider. Please re-subscribe to your brokerage’s Live Concierge Service subscription by [date] to continue your service without interruption.'
          : 'We’ve transitioned to a new payment provider. Please re-subscribe to your Live Concierge Service subscription by [date] to continue your service without interruption.',
      canCloseBanner: false,
      onButtonClick: goToLCSSubscriptionFlow,
      buttonText: 'Confirm Subscription',
    });
  }

  if (banners.length === 0) return null;

  return (
    <>
      <OverheadContainer>
        {banners.map((props, index) => (
          <AlertBanner key={index} {...props} />
        ))}
      </OverheadContainer>
    </>
  );
};

export default BillingAlertBanners;

const OverheadContainer = styled.div`
  display: grid;
  padding: 16px;
  gap: 8px;
`;
